import React, { useState, useLayoutEffect, useRef } from "react";
import { observe } from "selector-observer";
import { render } from "react-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, A11y, Pagination } from "swiper/modules";
import useMediaQuery from "../../../../scripts/react/hooks/useMediaQuery";

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface OpenInterval {
    Start: string;
    End: string;
}

interface DaySchedule {
    IsClosed: boolean;
    OpenIntervals: OpenInterval[];
}

type HoursData = Partial<Record<string, DaySchedule>>;

interface LocationCardProps {
    imageSrc?: string;
    imageAlt?: string;
    name?: string;
    pagesAndAnswersName?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    ctaLink?: string;
    ctaLabel?: string;
    ctaTarget?: string;
    dataEventId?: string;
    hours?: HoursData;
    mainPhone?: string;
    additionalHoursText?: string;
}

interface LocationCardsProps {
    disableAnimation?: boolean;
    cards: LocationCardProps[];
}

interface HoursStatus {
    isOpen: boolean;
    statusText: string;
    today: string;
}

interface InfoRowProps {
    icon: string;
    children: React.ReactNode;
}

const parseMinutes = (time: string): number => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
};

const formatTime = (time: string): string => {
    const [h, m] = time.split(':').map(Number);
    const period = h < 12 ? 'AM' : 'PM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
};

const getHoursStatus = (hours: HoursData): HoursStatus => {
    const now = new Date();
    const today = now.toLocaleDateString('en-US', { weekday: 'long' });
    const todayIndex = DAYS_OF_WEEK.indexOf(today);
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const todaySchedule = hours[today];
    if (todaySchedule && !todaySchedule.IsClosed && todaySchedule.OpenIntervals?.length > 0) {
        let currentInterval: OpenInterval | undefined;
        for (let j = 0; j < todaySchedule.OpenIntervals.length; j++) {
            const interval = todaySchedule.OpenIntervals[j];
            if (currentMinutes >= parseMinutes(interval.Start) && currentMinutes <= parseMinutes(interval.End)) {
                currentInterval = interval;
                break;
            }
        }
        if (currentInterval) {
            const is24Hours = currentInterval.Start === '00:00' && currentInterval.End === '23:59';
            const statusText = is24Hours ? '— 24 hours' : `— Closes ${formatTime(currentInterval.End)}`;
            return { isOpen: true, statusText, today };
        }
        let nextIntervalToday: OpenInterval | undefined;
        for (let j = 0; j < todaySchedule.OpenIntervals.length; j++) {
            const interval = todaySchedule.OpenIntervals[j];
            if (currentMinutes < parseMinutes(interval.Start)) {
                nextIntervalToday = interval;
                break;
            }
        }
        if (nextIntervalToday) {
            return { isOpen: false, statusText: `Opens today at ${formatTime(nextIntervalToday.Start)}`, today };
        }
    }

    for (let i = 1; i <= 7; i++) {
        const nextIndex = (todayIndex + i) % 7;
        const nextDay = DAYS_OF_WEEK[nextIndex];
        const nextSchedule = hours[nextDay];
        if (nextSchedule && !nextSchedule.IsClosed && nextSchedule.OpenIntervals?.length > 0) {
            const label = i === 1 ? 'tomorrow' : nextDay;
            return { isOpen: false, statusText: `until ${label} at ${formatTime(nextSchedule.OpenIntervals[0].Start)}`, today };
        }
    }

    return { isOpen: false, statusText: 'Temporarily closed', today };
};

const formatPhone = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11 && digits[0] === '1') return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    return phone;
};

const formatDayHours = (schedule: DaySchedule | undefined): string => {
    if (!schedule || schedule.IsClosed) return 'Closed';
    const intervals = schedule.OpenIntervals;
    if (!intervals || intervals.length === 0) return 'Closed';
    if (intervals[0].Start === '00:00' && intervals[0].End === '23:59') return 'Open 24 hours';
    return intervals.map(({ Start, End }) => `${formatTime(Start)} – ${formatTime(End)}`).join(', ');
};

const InfoRow: React.FC<InfoRowProps> = ({ icon, children }) => (
    <div className="tw-flex tw-w-full tw-bg-white tw-p-4 tw-items-center tw-gap-3 tw-shadow-md tw-rounded-md tw-duration-300 hover:tw-bg-accentCream focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-accentCream">
        <i className={`${icon} tw-text-2xl`} />
        {children}
    </div>
);

export const LocationCard: React.FC<LocationCardProps> = (props) => {
    const {
        imageSrc,
        imageAlt,
        name,
        pagesAndAnswersName,
        addressLine1,
        addressLine2,
        addressLine3,
        ctaLink,
        ctaLabel,
        ctaTarget,
        dataEventId,
        hours,
        mainPhone,
        additionalHoursText
    } = props;

    const hoursStatus = hours ? getHoursStatus(hours) : null;

    return (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-h-full">
            <img alt={imageAlt} src={imageSrc} className="tw-mb-6 tw-rounded-md" />
            <header className="tw-mb-3 xl:tw-mb-6 tw-min-h-[4rem] xl:tw-min-h-[5.25rem]">
                {name ? <h3 className="tw-line-clamp-2">{name}</h3> : <h3 className="tw-line-clamp-2">{pagesAndAnswersName}</h3>}
            </header>
            <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-3">
                <InfoRow icon="fa-solid fa-location-dot">
                    <address className="tw-text-lg tw-leading-normal">
                        {addressLine1 && <>{addressLine1}<br /></>}
                        {addressLine2 && <>{addressLine2}<br /></>}
                        {addressLine3 && <>{addressLine3}<br /></>}
                    </address>
                </InfoRow>
                {hoursStatus && (
                    <details className="tw-bg-white tw-shadow-md tw-rounded-md tw-group">
                        <summary className="tw-flex tw-text-lg tw-p-4 tw-items-center tw-cursor-pointer tw-rounded-md tw-duration-300 hover:tw-bg-accentCream focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-accentCream">
                            <span className={`tw-flex tw-items-center tw-mr-1 tw-font-semibold ${hoursStatus.isOpen ? 'tw-text-coreDarkGreen' : 'tw-text-textRed'}`}>
                                <i className="fa-regular fa-clock tw-text-2xl tw-mr-2" />
                                {hoursStatus.isOpen ? 'Open' : 'Closed'}
                            </span>
                            {hoursStatus.statusText}
                            <i className="fa-solid fa-chevron-down tw-ml-auto tw-transition-transform group-open:tw-rotate-180" />
                        </summary>
                        {additionalHoursText && (
                            <p className="tw-px-4 tw-pb-4 tw-text-lg">
                                {additionalHoursText}
                            </p>
                        )}
                        <dl className="tw-text-lg tw-px-4 tw-pb-4 tw-grid tw-grid-cols-2 tw-gap-4">
                            {DAYS_OF_WEEK.map(day => (
                                <React.Fragment key={day}>
                                    <dt className={day === hoursStatus.today ? 'tw-font-bold' : 'tw-font-medium'}>
                                        {day}
                                    </dt>
                                    <dd className={day === hoursStatus.today ? 'tw-font-bold' : ''}>
                                        {formatDayHours(hours?.[day])}
                                    </dd>
                                </React.Fragment>
                            ))}
                        </dl>
                    </details>
                )}
                {mainPhone && (
                    <a className="tw-text-lg" href={`tel:${mainPhone}`} aria-label={`Call ${formatPhone(mainPhone)}`}>
                        <InfoRow icon="fa-solid fa-phone">
                            {formatPhone(mainPhone)}
                        </InfoRow>
                    </a>
                )}
                {ctaLink && (
                    <a data-event-id={dataEventId || null} title={ctaLabel} aria-label={ctaLabel} href={ctaLink} className="btn" target={ctaTarget}>
                        <InfoRow icon="fa-solid fa-circle-info">
                            <span className="btn-label">{ctaLabel}</span>
                            <i className="fa-solid fa-arrow-right" />
                        </InfoRow>
                    </a>
                )}
            </div>
        </div>
    );
};

const getContainerOffset = (): number => {
    const vw = window.innerWidth;
    if (vw >= 1200) return Math.round(vw * 0.135);
    if (vw >= 768) return 20;
    return 16;
};

export const LocationCards: React.FC<LocationCardsProps> = ({ cards }) => {
    const isDesktop = useMediaQuery("(min-width: 1200px)");
    const [slidesOffset, setSlidesOffset] = useState(getContainerOffset);

    useLayoutEffect(() => {
        const handleResize = () => setSlidesOffset(getContainerOffset());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!cards?.length) return null;
    return (
        <Swiper
            className="tw-my-4"
            modules={[Navigation, Keyboard, A11y, Pagination]}
            slidesPerView={1.25}
            spaceBetween={40}
            slidesOffsetBefore={slidesOffset}
            grabCursor={true}
            keyboard={{ enabled: true }}
            a11y={{ enabled: true }}
            pagination={{ clickable: true }}
            navigation={isDesktop}
            breakpoints={{
                464: { slidesPerView: 1.5 },
                768: { slidesPerView: 2.25 },
                1200: { slidesPerView: 3 },
                1600: { slidesPerView: 3.5 },
            }}
        >
            {cards.map((card, index) => (
                <SwiperSlide key={index}>
                    <LocationCard {...card} />
                </SwiperSlide>
            ))}
        </Swiper>
    );
};

document.addEventListener("readystatechange", () => {
    if (document.readyState === "interactive" || document.readyState === "complete") {
        observe('[data-component="locationcards"]', {
            add(element) {
                const { dataset } = element as HTMLElement;
                const { propsId } = dataset;
                const componentData = window.ucsdh[`locationcards_${propsId}`];
                render(<LocationCards {...componentData} />, element);
            },
        });
    }
});
